import React, { useEffect, useRef, useState } from 'react';
import { resolveRequestErrorCode } from '$/api/utils/resolveRequestErrorMessage';
import NextImage from '$/components/NextImage';
import { useOfficialStore } from '$/store/useOfficialStore';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import { isTaskInsufficientBalanceError } from '$/pages/RoboneoTeamFlow/utils/taskError';
import { openInsufficientRadishModal } from '$/store/useVipModalStore';
import { getMyIpProfileDisplay } from '$/domains/myIp/profileDisplay';
import { getMyIpMediaReferences, getMyIpMediaReferenceUrl } from '$/domains/myIp/mediaReferences';
import {
  trackMyIpDetailViewClick, trackMyIpRelationCardClick, trackMyIpRelationCardShow, trackMyIpRoleCardClick,
} from '$/domains/myIp/tracking';
import type { MyIpItem } from '$/domains/myIp/types';
import { useMyIpMediaPreview } from '$/components/MyIpMediaPreview/useMyIpMediaPreview';
import { MyIpMediaPreviewBoundary } from '$/components/MyIpMediaPreview/Boundary';
import { useCardAction } from '$/pages/RoboneoMyIps/useCardAction';
import { useMyIpCopy } from '$/pages/RoboneoMyIps/useMyIpCopy';
import radishIcon from '$/components/CalcRadish/radish.png';
import RadishCostText from '$/pages/RoboneoTeamFlow/components/RadishCostText';
import {
  generateMyIpRoleCardImage,
  getMyIpRoleCardHistory,
  getMyIpRoleCardCost,
  resumeMyIpRoleCardImage,
  type MyIpRoleCardCost,
  type MyIpRoleCardGenerationResult,
} from '$/domains/myIp/roleCardGeneration';
import useTaskSpeedStore from '$/store/useTaskSpeedStore';
import { navigateToMyIpReturnHref } from '@/utils/myIp/navigateReturn';
import { navigate } from '@/common/agents';
import type { MyIpDetailCopy, MyIpDetailViewProps } from './types';
import MyIpEditForm from './EditForm';
import MyIpRoleCardModal from './RoleCardModal';
import styles from './index.module.scss';

/** 国内 IP 图片使用服务端下发的水印地址；海外和未下发水印时展示原图。 */
function DetailImage({
  src, alt, fallbackLabel, className, useWatermark = false, watermarkSrc,
}: {
  src?: string;
  alt: string;
  fallbackLabel?: string;
  className?: string;
  useWatermark?: boolean;
  /** 服务端下发的水印图；有值时直接展示。 */
  watermarkSrc?: string;
}) {
  const [failed, setFailed] = useState<string>();
  if (!src || failed === src) {
    return (
      <div className={`${className ?? ''} ${styles.coverPlaceholder}`} role="img" aria-label={fallbackLabel ?? alt}>
        <IconFont name="portraitPhotoBold" fontSize={40} aria-hidden />
      </div>
    );
  }
  const displaySrc = useWatermark && watermarkSrc ? watermarkSrc : src;
  return (
    <NextImage
      src={displaySrc}
      alt={alt}
      title=""
      className={className}
      crossOrigin="anonymous"
      useOriginalImage
      preserveSourceFormat
      onError={() => setFailed(src)}
    />
  );
}

/** 关联项目封面保留服务端签名原址；无图或加载失败复用 IP 卡片的无图占位，项目链接仍可用。 */
function ProjectCover({ src, label }: { src: string; label: string; }) {
  const [failed, setFailed] = useState<string>();
  if (!src.trim() || failed === src) {
    return (
      <span className={`${styles.projectCover} ${styles.projectPlaceholder}`} role="img" aria-label={label}>
        <IconFont name="portraitPhotoBold" fontSize={64} aria-hidden />
      </span>
    );
  }
  return <NextImage src={src} alt="" className={styles.projectCover} crossOrigin="anonymous" useOriginalImage preserveSourceFormat onError={() => setFailed(src)} />;
}

/** 音色显示在设定栏；仅用户激活后播放，展开真实进度并保留失败重试。 */
function VoicePlayer({
  url, name, copy, onPlayClick,
}: { url: string; name: string; copy: MyIpDetailCopy; onPlayClick?: () => void; }) {
  const audio = useRef<HTMLAudioElement>(null);
  const mounted = useRef(true);
  const [playing, setPlaying] = useState(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const pendingRef = useRef(false);
  const playVersion = useRef(0);
  useEffect(() => {
    mounted.current = true;
    const element = audio.current;
    return () => { mounted.current = false; playVersion.current += 1; element?.pause(); };
  }, []);

  /** 媒体错误可能先于 play Promise 拒绝到达，立即恢复按钮并失效旧 Promise。 */
  const handleError = () => {
    playVersion.current += 1;
    pendingRef.current = false;
    setPending(false);
    setPlaying(false);
    setFailed(true);
  };

  /** 捕获浏览器自动播放限制及媒体错误，不能留下永久 loading 的按钮。 */
  const toggle = async () => {
    const element = audio.current;
    if (!element || pendingRef.current) return;
    if (!element.paused) {
      element.pause();
      setPlaying(false);
      return;
    }
    onPlayClick?.();
    pendingRef.current = true;
    playVersion.current += 1;
    const version = playVersion.current;
    setPending(true);
    setFailed(false);
    try {
      await element.play();
      if (mounted.current && playVersion.current === version) setPlaying(!element.paused);
    } catch {
      if (mounted.current && playVersion.current === version) { setFailed(true); setPlaying(false); }
    } finally {
      if (playVersion.current === version) {
        pendingRef.current = false;
        if (mounted.current) setPending(false);
      }
    }
  };

  /** 媒体元数据可能暂为 Infinity/NaN，未就绪时禁用拖动且不显示虚假总时长。 */
  const updateDuration = () => {
    const value = audio.current?.duration ?? 0;
    setDuration(Number.isFinite(value) && value > 0 ? value : 0);
  };

  /** 进度只反映浏览器实际播放位置，并允许键盘方向键调整。 */
  const seek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const element = audio.current;
    if (!element || !duration) return;
    const value = Math.min(duration, Math.max(0, Number(event.target.value)));
    element.currentTime = value;
    setPosition(value);
  };

  /** 以真实秒数显示分秒，小时级历史音频也不截断分钟。 */
  const formatTime = (value: number) => `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}`;

  return (
    <div className={styles.voice}>
      {/* 音色控件提供本地化标签，音频元素本身不进入键盘顺序。 */}
      {/* IP 素材契约未提供字幕轨，不能创建虚假的 caption 资源。 */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audio}
        crossOrigin="anonymous"
        src={url}
        preload="metadata"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onError={handleError}
        onLoadedMetadata={updateDuration}
        onDurationChange={updateDuration}
        onTimeUpdate={() => setPosition(audio.current?.currentTime || 0)}
        onEmptied={() => { setDuration(0); setPosition(0); }}
      />
      <button type="button" className={styles.voiceControl} onClick={toggle} disabled={pending} aria-busy={pending} aria-label={playing ? copy.pauseVoice : copy.playVoice}>
        <span aria-hidden="true"><IconFont name="musicV3Fill" fontSize={16} /></span>
        <span className={styles.voiceName} title={name}>{name}</span>
        <span aria-hidden="true"><IconFont name={playing ? 'pauseFill' : 'playFill'} fontSize={16} /></span>
      </button>
      <div className={styles.voiceTimeline} hidden={!playing && position === 0}>
        <input type="range" min={0} max={duration || 1} step={0.1} value={Math.min(position, duration || 0)} disabled={!duration} aria-label={copy.voiceProgress} onChange={seek} />
        <span>{`${formatTime(position)} / ${duration ? formatTime(duration) : '--:--'}`}</span>
      </div>
      {failed && <span className={styles.mediaError} role="status">{copy.mediaError}</span>}
    </div>
  );
}

/** 读取真实 HTTP 状态；IP 详情业务码由 errorLabel 单独映射，避免混淆权限与不存在。 */
function errorStatus(error: unknown) {
  const data = error && typeof error === 'object' ? error as { status?: number; response?: { status?: number; }; } : {};
  return data.status ?? data.response?.status;
}

/** 非本人个人 IP：接口 403/15602 与创建者校验失败共用同一空态。 */
function isPrivateIpError(error: unknown) {
  return resolveRequestErrorCode(error) === '15602' || errorStatus(error) === 403;
}

/** 映射明确的 HTTP 状态和 IP 详情业务码；诊断错误原文不会直接显示给用户。 */
function errorLabel(error: unknown, copy: MyIpDetailCopy): string {
  if (isPrivateIpError(error)) return copy.forbidden;
  if (errorStatus(error) === 404) return copy.notFound;
  return copy.loadError;
}

/** Figma 57444:114643：四角星空态 + 返回首页，不提供重试以免误暴露他人资产。 */
function PrivateIpEmpty({ copy, homeHref }: { copy: MyIpDetailCopy; homeHref: string; }) {
  return (
    <div className={styles.forbidden}>
      <svg className={styles.forbiddenIconGradient} width={0} height={0} aria-hidden>
        <defs>
          <linearGradient id="ip-forbidden-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--effects-gradient1-1, #fff)" />
            <stop offset="33%" stopColor="var(--effects-gradient1-2, #8ea8fe)" />
            <stop offset="66%" stopColor="var(--effects-gradient1-3, #f899ff)" />
            <stop offset="100%" stopColor="var(--effects-gradient1-1, #fff)" />
          </linearGradient>
        </defs>
      </svg>
      <div className={styles.forbiddenContent}>
        <div className={styles.forbiddenIconWrap}>
          <IconFont name="fourPointedStarFill" fontSize={20} color="currentColor" className={styles.forbiddenStar} aria-hidden />
        </div>
        <div className={styles.forbiddenText}>
          <p className={styles.forbiddenTitle} role="alert">{copy.forbidden}</p>
          <p className={styles.forbiddenDescription}>{copy.privateDescription}</p>
        </div>
      </div>
      <a className={styles.forbiddenHome} href={homeHref}>{copy.returnHome}</a>
    </div>
  );
}

/** 询价期间展示加载态，成功后展示折扣价、原价与萝卜图标。 */
function RoleCardCostDisplay({ loading, value }: { loading: boolean; value?: MyIpRoleCardCost; }) {
  let content: React.ReactNode = null;
  if (loading) {
    content = <IconFont name="loadingCircleBold" fontSize={16} color="currentColor" className={styles.spinningIcon} />;
  } else if (value) {
    content = (
      <>
        <RadishCostText cost={value.cost} discountCost={value.discountCost} />
        <img src={radishIcon.src} width={16} height={16} alt="" aria-hidden="true" crossOrigin="anonymous" />
      </>
    );
  }
  return <span className={styles.roleCardCost}>{content}</span>;
}

/** 角色卡去重键忽略签名参数，避免同一资源不同签发地址被当成两张卡。 */
function getRoleCardDedupUrlKey(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split(/[?#]/)[0] ?? url;
  }
}

/** 合并历史查询与本次刚生成的结果；优先按 taskId，其次按去签 URL，避免转存前后地址不同导致重复。 */
function mergeRoleCardResults(
  primary: MyIpRoleCardGenerationResult[],
  secondary: MyIpRoleCardGenerationResult[],
): MyIpRoleCardGenerationResult[] {
  const seenTaskIds = new Set<string>();
  const seenUrls = new Set<string>();
  const merged: MyIpRoleCardGenerationResult[] = [];
  [...primary, ...secondary].forEach((result) => {
    const urlKey = result.url ? getRoleCardDedupUrlKey(result.url) : '';
    if (result.taskId && seenTaskIds.has(result.taskId)) return;
    if (urlKey && seenUrls.has(urlKey)) return;
    if (result.taskId) seenTaskIds.add(result.taskId);
    if (urlKey) seenUrls.add(urlKey);
    merged.push(result);
  });
  return merged;
}

/** 顶部操作独立于页签内容，方形主图与可滚动设定共用授权媒体解析。 */
function DetailContent({ item, ...props }: MyIpDetailViewProps & { item: MyIpItem; copy: MyIpDetailCopy; }) {
  const { media: mediaCopy, roleCard: roleCardCopy } = useMyIpCopy();
  const isOversea = useOfficialStore((current) => current.isOversea());
  const useWatermark = !isOversea;
  const {
    copy, media, state, onCreateWithIp, onEditIp, onLoadProjects, getProjectHref, unavailableReason, onSave,
    locale = 'zh-CN', hideHeaderActions = false,
  } = props;
  const profile = getMyIpProfileDisplay(item.profile);
  // 优先展示已保存预设音色的真实名称，保留历史资产引用和播放文案兜底。
  const voiceName = media.voiceName || getMyIpMediaReferences(item).references.find(
    (reference) => reference.role === 'voice' && reference.type === 'audio'
      && getMyIpMediaReferenceUrl(reference, media.resourceUrls) === media.voiceUrl,
  )?.name || copy.playVoice;
  const action = useCardAction(onCreateWithIp ? () => onCreateWithIp(item) : undefined);
  const editAction = useCardAction(onEditIp ? async () => {
    trackMyIpDetailViewClick(item, 'edit');
    await onEditIp(item);
  } : undefined);
  const [failedVideo, setFailedVideo] = useState<string>();
  const [editing, setEditing] = useState(false);
  const [roleCardOpen, setRoleCardOpen] = useState(false);
  const [roleCardGenerating, setRoleCardGenerating] = useState(false);
  const [roleCardChecking, setRoleCardChecking] = useState(true);
  const [roleCardResults, setRoleCardResults] = useState<MyIpRoleCardGenerationResult[]>([]);
  const [roleCardHistoryLoading, setRoleCardHistoryLoading] = useState(false);
  const roleCardAbortRef = useRef<AbortController>();
  const roleCardResumeAbortRef = useRef<AbortController>();
  const roleCardHistoryAbortRef = useRef<AbortController>();
  const [roleCardCost, setRoleCardCost] = useState<MyIpRoleCardCost>();
  const [roleCardCostLoading, setRoleCardCostLoading] = useState(true);
  const [previewFailure, setPreviewFailure] = useState<string>();
  const exposedProjectCards = useRef(new Set<string>());
  const taskSpeed = useTaskSpeedStore((current) => current.taskSpeed);
  const images = [
    {
      id: 'main',
      kind: 'image',
      url: media.coverUrl || '',
      name: item.name,
      watermarkUrl: useWatermark ? media.coverWatermarkUrl : undefined,
    },
    ...media.threeViewUrls.map((url, index) => ({
      id: `three_view:${url}`,
      kind: 'image',
      url,
      name: copy.threeViews,
      watermarkUrl: useWatermark ? media.threeViewWatermarkUrls?.[index] : undefined,
    })),
  ];
  // 详情页不再切换「基础设定 / 更多素材」，始终展示完整设定内容。
  const previewImage = useMyIpMediaPreview(!editing ? images : [], {
    scopeKey: state.contextKey,
    onMediaError: setPreviewFailure,
    resolveOriginalDownloadUrlOnly: true,
  });
  const showVideo = Boolean(media.videoUrl && media.videoUrl !== failedVideo);
  const projectsBusy = state.projectsStatus === 'loading';
  const projectsLabel = state.projectsStatus === 'error' ? copy.retry : copy.loadMoreProjects;
  const roleCardBusy = roleCardChecking || roleCardGenerating;
  const coverPoster = useWatermark && media.coverWatermarkUrl ? media.coverWatermarkUrl : media.coverUrl;
  /** 角色卡数据接口仅支持个人 IP，官方角色不可发起生成。萝卜不足时打开订阅弹窗，不走通用失败 toast。 */
  const handleGenerateRoleCard = async () => {
    if (item.assetType !== 'personal' || roleCardBusy || roleCardAbortRef.current) return;
    trackMyIpRoleCardClick(item, '', roleCardCost?.discountCost ?? roleCardCost?.cost ?? '');
    const abort = new AbortController();
    roleCardAbortRef.current = abort;
    setRoleCardGenerating(true);
    try {
      const result = await generateMyIpRoleCardImage(item.ipId, abort.signal);
      if (abort.signal.aborted || roleCardAbortRef.current !== abort) return;
      setRoleCardResults((previous) => mergeRoleCardResults([result], previous));
      setRoleCardOpen(true);
    } catch (error) {
      if (!abort.signal.aborted && roleCardAbortRef.current === abort) {
        if (isTaskInsufficientBalanceError(error)) {
          openInsufficientRadishModal();
        } else {
          message.error(roleCardCopy.loadError);
        }
      }
    } finally {
      if (!abort.signal.aborted && roleCardAbortRef.current === abort) {
        setRoleCardGenerating(false);
        roleCardAbortRef.current = undefined;
      }
    }
  };
  /** 查询当前 IP 的全部角色卡历史并打开弹框，不依赖本次是否刚生成。 */
  const handleViewRoleCard = async () => {
    if (item.assetType !== 'personal' || roleCardHistoryLoading) return;
    trackMyIpDetailViewClick(item, 'look_ip_role');
    roleCardHistoryAbortRef.current?.abort();
    const abort = new AbortController();
    roleCardHistoryAbortRef.current = abort;
    setRoleCardOpen(true);
    setRoleCardHistoryLoading(true);
    try {
      const results = await getMyIpRoleCardHistory(item.ipId, item.name, abort.signal);
      if (abort.signal.aborted || roleCardHistoryAbortRef.current !== abort) return;
      setRoleCardResults((previous) => mergeRoleCardResults(results, previous));
    } catch {
      if (!abort.signal.aborted && roleCardHistoryAbortRef.current === abort) message.error(roleCardCopy.loadError);
    } finally {
      if (!abort.signal.aborted && roleCardHistoryAbortRef.current === abort) {
        setRoleCardHistoryLoading(false);
        roleCardHistoryAbortRef.current = undefined;
      }
    }
  };
  /** 关闭角色卡弹框并终止尚未完成的历史查询。 */
  const handleCloseRoleCard = () => {
    setRoleCardOpen(false);
    roleCardHistoryAbortRef.current?.abort();
    roleCardHistoryAbortRef.current = undefined;
    setRoleCardHistoryLoading(false);
  };
  /** 切换角色或卸载详情时终止旧生成任务，避免迟到结果打开错误角色弹框。 */
  useEffect(() => {
    setRoleCardOpen(false);
    setRoleCardResults([]);
    setRoleCardGenerating(false);
    setRoleCardChecking(true);
    setRoleCardHistoryLoading(false);
    return () => {
      roleCardAbortRef.current?.abort();
      roleCardAbortRef.current = undefined;
      roleCardHistoryAbortRef.current?.abort();
      roleCardHistoryAbortRef.current = undefined;
    };
  }, [item.ipId]);
  /** 每次进入 IP 详情都查询专属房间的最新任务，并在发现进行中任务时继续轮询。 */
  useEffect(() => {
    // 弹框只读态不恢复角色卡任务，避免自动弹出角色卡结果。
    if (hideHeaderActions || item.assetType !== 'personal') {
      setRoleCardChecking(false);
      return undefined;
    }
    const abort = new AbortController();
    roleCardResumeAbortRef.current = abort;
    setRoleCardChecking(true);
    resumeMyIpRoleCardImage(item.ipId, item.name, abort.signal)
      .then((result) => {
        if (abort.signal.aborted || roleCardResumeAbortRef.current !== abort) return;
        if (result) {
          setRoleCardResults((previous) => mergeRoleCardResults([result], previous));
          setRoleCardOpen(true);
        }
      })
      .catch(() => {
        if (!abort.signal.aborted && roleCardResumeAbortRef.current === abort) message.error(roleCardCopy.loadError);
      })
      .finally(() => {
        if (!abort.signal.aborted && roleCardResumeAbortRef.current === abort) {
          setRoleCardChecking(false);
          roleCardResumeAbortRef.current = undefined;
        }
      });
    return () => {
      abort.abort();
      if (roleCardResumeAbortRef.current === abort) roleCardResumeAbortRef.current = undefined;
    };
  }, [hideHeaderActions, item.assetType, item.ipId, item.name, roleCardCopy.loadError]);
  /** 仅个人 IP 加载角色卡数据询价，官方 IP 不调用个人资产接口。 */
  useEffect(() => {
    // 弹框隐藏生成入口时不再询价，避免多余请求。
    if (hideHeaderActions || item.assetType !== 'personal') {
      setRoleCardCost(undefined);
      setRoleCardCostLoading(false);
      return undefined;
    }
    const abort = new AbortController();
    setRoleCardCost(undefined);
    setRoleCardCostLoading(true);
    getMyIpRoleCardCost(item.ipId, abort.signal)
      .then((cost) => {
        if (!abort.signal.aborted) setRoleCardCost(cost);
      })
      .catch(() => {
        if (!abort.signal.aborted) setRoleCardCost(undefined);
      })
      .finally(() => {
        if (!abort.signal.aborted) setRoleCardCostLoading(false);
      });
    return () => abort.abort();
  }, [hideHeaderActions, item.ipId, item.assetType, taskSpeed]);
  useEffect(() => {
    /** 弹框不展示关联项目，也不补报卡片曝光。 */
    if (hideHeaderActions) return;
    /** 关联项目列表追加分页时只为新出现的卡片补报曝光。 */
    state.projects.forEach((project, index) => {
      const key = project.projectId || `${project.roomId}:${project.canvasId}`;
      if (exposedProjectCards.current.has(key)) return;
      exposedProjectCards.current.add(key);
      // Dayu 8131：关联项目卡片 position 从 0 开始。
      trackMyIpRelationCardShow(item, index, project);
    });
  }, [hideHeaderActions, item, state.projects]);
  if (editing && onSave) return <MyIpEditForm item={item} locale={locale} onSave={onSave} onCancel={() => setEditing(false)} />;
  return (
    <>
      <div className={styles.detailPanel}>
        {!hideHeaderActions && (
        <div className={styles.panelHeader}>
          <div className={styles.createAction}>
            {item.assetType === 'personal' && (
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={!onEditIp || editAction.pending}
              onClick={editAction.run}
              aria-busy={editAction.pending}
            >
              {editAction.pending ? copy.loading : copy.editAgent}
            </button>
            )}
            {item.assetType === 'personal' && (
            <button
              type="button"
              className={`${styles.secondaryButton} ${styles.viewRoleCardButton}`}
              disabled={roleCardHistoryLoading}
              onClick={handleViewRoleCard}
              aria-busy={roleCardHistoryLoading}
              aria-haspopup="dialog"
              aria-expanded={roleCardOpen}
            >
              <IconFont name="eyeFill" fontSize={16} />
              <span>{roleCardCopy.view}</span>
            </button>
            )}
            {item.assetType === 'personal' && (
            <button
              type="button"
              className={`${styles.secondaryButton} ${styles.roleCardButton}`}
              disabled={roleCardBusy}
              onClick={handleGenerateRoleCard}
              aria-busy={roleCardBusy}
            >
              <span>{mediaCopy.generateCard}</span>
              {/* 询价与生成共用右侧费用槽：busy 时也走 RoleCardCostDisplay，避免裸 Icon 落在文案左侧。 */}
              <RoleCardCostDisplay
                loading={roleCardBusy || roleCardCostLoading}
                value={roleCardBusy ? undefined : roleCardCost}
              />
            </button>
            )}
            <button type="button" className={styles.primaryButton} disabled={!onCreateWithIp || action.pending} onClick={action.run} aria-busy={action.pending}>
              {action.pending ? copy.loading : copy.createWithIp}
            </button>
          </div>
        </div>
        )}
        {!hideHeaderActions && editAction.failed && <p role="alert">{copy.actionError}</p>}
        {!hideHeaderActions && item.assetType === 'personal' && (
          <MyIpRoleCardModal
            open={roleCardOpen}
            ipId={item.ipId}
            results={roleCardResults}
            backImage={media.coverUrl}
            useWatermark={useWatermark}
            loading={roleCardHistoryLoading}
            onClose={handleCloseRoleCard}
          />
        )}
        {!hideHeaderActions && !onCreateWithIp && <p>{unavailableReason}</p>}
        {!hideHeaderActions && action.failed && <p role="alert">{copy.actionError}</p>}
        <div className={styles.columns}>
          <section className={styles.hero} aria-label={item.name}>
            {showVideo ? (
            // IP 素材契约未提供字幕轨，保留真实媒体控制，不伪造字幕资源。
            // eslint-disable-next-line jsx-a11y/media-has-caption
              <video className={styles.heroMedia} crossOrigin="anonymous" src={media.videoUrl} poster={coverPoster} controls preload="metadata" onError={() => setFailedVideo(media.videoUrl)} />
            ) : (
              <button type="button" className={styles.imagePreviewButton} disabled={!media.coverUrl} aria-label={mediaCopy.enlargeMain} onClick={() => previewImage(images[0])}>
                <DetailImage className={styles.heroMedia} src={media.coverUrl} watermarkSrc={media.coverWatermarkUrl} useWatermark={useWatermark} alt={item.name} fallbackLabel={copy.coverUnavailable} />
              </button>
            )}
          </section>
          <div className={styles.settingsFrame}>
            {/* 独立滚动区必须可聚焦，键盘用户才能用方向键/PageDown 阅读完整设定。 */}
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div className={styles.settings} role="region" aria-label={copy.characterSettings} tabIndex={0}>
              <header className={styles.summary}>
                <div className={styles.identity}>
                  <DetailImage src={media.avatarUrl} watermarkSrc={media.avatarWatermarkUrl} useWatermark={useWatermark} alt="" fallbackLabel={item.name} className={styles.avatar} />
                  <div className={styles.identityText}>
                    <h1>{item.name}</h1>
                    {profile.identity && <p>{`${copy.identity}：${profile.identity}`}</p>}
                  </div>
                </div>
              </header>
              {media.voiceUrl && <VoicePlayer key={media.voiceUrl} url={media.voiceUrl} name={voiceName} copy={copy} onPlayClick={() => trackMyIpDetailViewClick(item, 'timbre')} />}
              {profile.persona && profile.personality.join('\n') !== profile.persona && (
              <section className={styles.settingCard}>
                <h2>{copy.persona}</h2>
                <p>{profile.persona}</p>
              </section>
              )}
              {media.threeViewUrls.length > 0 && (
              <section className={styles.settingCard}>
                <h3>{copy.threeViews}</h3>
                  {media.threeViewUrls.map((url, index) => <button type="button" key={url} className={styles.threeViewButton} aria-label={mediaCopy.enlargeThreeView} onClick={() => previewImage(images[index + 1])}><DetailImage src={url} watermarkSrc={media.threeViewWatermarkUrls?.[index]} useWatermark={useWatermark} alt={copy.threeViews} className={styles.threeView} /></button>)}
              </section>
              )}
              {profile.personality.length > 0 && (
              <section className={styles.settingCard}>
                <h3>{copy.personality}</h3>
                <p>{profile.personality.join('、')}</p>
              </section>
              )}
              {profile.appearance && (
              <section className={styles.settingCard}>
                <h3>{copy.appearance}</h3>
                <p>{profile.appearance}</p>
              </section>
              )}
              {profile.visualStyle && (
              <section className={styles.settingCard}>
                <h3>{copy.visualStyle}</h3>
                <p>{profile.visualStyle}</p>
              </section>
              )}
              {profile.speechStyle && (
              <section className={styles.settingCard}>
                <h3>{copy.speechStyle}</h3>
                <p>{profile.speechStyle}</p>
              </section>
              )}
              {profile.backstory && (
              <section className={styles.settingCard}>
                <h2>{copy.backstory}</h2>
                <p>{profile.backstory}</p>
              </section>
              )}
              {typeof item.profile.language_style === 'string' && item.profile.language_style && item.profile.language_style !== profile.speechStyle && (
              <section className={styles.settingCard}>
                <h2>{copy.speechStyle}</h2>
                <p>{item.profile.language_style}</p>
              </section>
              )}
              {item.summary && <p className={styles.description}>{item.summary}</p>}
            </div>
          </div>
        </div>
      </div>
      {previewFailure && images.some((image) => image.url === previewFailure) && <p role="alert">{mediaCopy.unavailable}</p>}
      {!hideHeaderActions && item.assetType === 'personal' && (
      <section className={styles.projects}>
        <h2>
          {copy.relatedProjects}
          <span className={styles.projectCount}>{copy.formatProjectCount(state.projectTotal ?? item.referenceCount)}</span>
        </h2>
        {state.projects.length > 0 && (
        <ul className={styles.projectGrid}>
          {state.projects.map((project, index) => {
            const href = getProjectHref(project);
            const position = index;
            const content = (
              <>
                <ProjectCover src={project.coverUrl} label={copy.coverUnavailable} />
                <span className={styles.projectName} title={project.name}>{project.name}</span>
              </>
            );
            return (
              <li key={project.projectId}>
                {href ? (
                  <button
                    type="button"
                    className={styles.projectLink}
                    onClick={() => {
                      trackMyIpRelationCardClick(item, position, project);
                      // 用按钮导航避免 <a href> 在浏览器底部暴露链接地址。
                      navigate({ mode: 'url', url: href });
                    }}
                  >
                    {content}
                  </button>
                ) : (
                  <div>
                    {content}
                    <span className={styles.projectUnavailable}>{unavailableReason}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        )}
        {state.projectsStatus === 'success' && state.projects.length === 0 && <p>{copy.noProjects}</p>}
        {state.projectsStatus === 'error' && <p role="alert">{copy.loadError}</p>}
        {projectsBusy && <p role="status">{copy.loading}</p>}
        {state.projectsStatus === 'error' && (
        <button type="button" className={styles.secondaryButton} onClick={onLoadProjects} disabled={projectsBusy}>{projectsLabel}</button>
        )}
      </section>
      )}
    </>
  );
}

/** 详情保留权限门禁和宿主文案优先级；宿主可用自身顶部返回导航替代 SDK 返回栏。 */
export default function MyIpDetailView(props: MyIpDetailViewProps) {
  const localizedCopy = useMyIpCopy();
  const {
    state, copy = localizedCopy.detail, media, backHref, homeHref = '/home', onRetry, onLoadProjects, onCreateWithIp, onEditIp,
    getProjectHref, onSave, locale, onPageVisible, roleCardShareUrl,
    unavailableReason = localizedCopy.unavailableReason, hideBackNavigation = false, hideHeaderActions = false,
  } = props;
  /** 备用返回栏与顶部导航一致，按来源完整路径替换当前记录。 */
  const handleBack = () => {
    navigateToMyIpReturnHref(backHref);
  };
  const exposure = useRef({ key: state.contextKey, sent: false });
  if (exposure.current.key !== state.contextKey) exposure.current = { key: state.contextKey, sent: false };
  useEffect(() => {
    if (state.status === 'success' && state.item && !exposure.current.sent && onPageVisible) {
      exposure.current.sent = true;
      onPageVisible();
    }
  }, [state.status, state.item, state.contextKey, onPageVisible]);
  if (state.status === 'idle' || state.status === 'denied') return null;
  const failed = state.status === 'invalid' || state.status === 'error';
  const forbidden = state.status === 'error' && isPrivateIpError(state.error);
  return (
    <MyIpMediaPreviewBoundary>
      <main className={`${styles.page}${forbidden ? ` ${styles.pageForbidden}` : ''}`}>
        {!hideBackNavigation && <div className={styles.backBar}><button type="button" className={styles.back} onClick={handleBack}>{copy.back}</button></div>}
        {state.status === 'loading' && <div className={styles.status} role="status" aria-busy="true">{copy.loading}</div>}
        {forbidden && <PrivateIpEmpty copy={copy} homeHref={homeHref} />}
        {failed && !forbidden && (
        <div className={styles.status}>
          <p role="alert">{state.status === 'invalid' ? copy.invalidIp : errorLabel(state.error, copy)}</p>
          {state.status === 'error' && <button type="button" className={styles.secondaryButton} onClick={onRetry}>{copy.retry}</button>}
        </div>
        )}
        {state.status === 'success' && state.item && (
          <DetailContent
            key={state.contextKey}
            state={state}
            copy={copy}
            media={media}
            backHref={backHref}
            onRetry={onRetry}
            onLoadProjects={onLoadProjects}
            onCreateWithIp={onCreateWithIp}
            onEditIp={onEditIp}
            getProjectHref={getProjectHref}
            item={state.item}
            unavailableReason={unavailableReason}
            onSave={onSave}
            locale={locale}
            roleCardShareUrl={roleCardShareUrl}
            hideHeaderActions={hideHeaderActions}
          />
        )}
      </main>
    </MyIpMediaPreviewBoundary>
  );
}
